/* eslint-disable */
// COPY of new-zing-monorepo/libs/shared/src/smartNav/decisionCore.ts — keep in sync.
// (Cross-repo submodule: the monorepo's "generate from shared source" build step cannot
// reach here, so this is a synced copy. Any change to the shared core must be mirrored.)
//
// Shared, framework-agnostic redirect decision core.
//
// SINGLE SOURCE OF TRUTH for "does the shell own this route right now?". Consumed by:
//   1. the shell bundle (smartNav/index.ts),
//   2. the inline early-decision script in index.html (esbuild-compiled + inlined by the
//      vite plugin — that is why this file must stay self-contained: NO imports),
//   3. the legacy app (which fetches the registry + polls Unleash and runs the same logic).
//
// Keep it dependency-free. `normalizePath` is duplicated here (instead of importing
// ../utils/normalizePath) precisely so the file can be inlined verbatim.

export type Verdict = 'shell' | 'legacy';

export interface DecisionInput {
  /** Full browser paths the shell-app owns (dynamic segments stripped). */
  registry: readonly string[];
  /** Unleash toggle map: flag name -> enabled. */
  toggles: Readonly<Record<string, boolean>>;
  /** Drives the flag-key prefix, e.g. "localhost" -> "LOCALHOST". */
  tenantDomain: string;
  /** Static always-to-legacy prefixes (mirrors VITE_LEGACY_PATH_PREFIXES). */
  escapeHatchPrefixes?: readonly string[];
  isAuthenticated?: boolean;
}

// Mirror of libs/shared/src/utils/normalizePath.ts — keep in sync.
export function normalizePath(pathname: string): string {
  const segments = pathname
    .split('/')
    .filter((s) => s.length > 0 && !s.startsWith(':'));
  return segments.length > 0 ? segments.join('_').toUpperCase() : 'ROOT';
}

/** Longest registered template the pathname falls under (or the raw pathname). */
export function resolveTemplatePath(pathname: string, registry: readonly string[]): string {
  let best = '';
  for (const template of registry) {
    if (
      (pathname === template || pathname.startsWith(template + '/')) &&
      template.length > best.length
    ) {
      best = template;
    }
  }
  return best || pathname;
}

function isRegistered(pathname: string, registry: readonly string[]): boolean {
  for (const p of registry) {
    if (pathname === p || pathname.startsWith(p + '/')) return true;
  }
  return false;
}

/**
 * Returns 'shell' iff: not an escape-hatch prefix AND global flag on AND per-route flag
 * on AND the path is owned by the shell registry. Otherwise 'legacy'.
 */
export function decide(pathname: string, input: DecisionInput): Verdict {
  const { registry, toggles, tenantDomain, escapeHatchPrefixes, isAuthenticated } = input;

  for (const prefix of escapeHatchPrefixes ?? []) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return 'legacy';
  }
  if (isAuthenticated === false) {
    return isRegistered(pathname, registry) ? 'shell' : 'legacy';
  }

  const domain = (tenantDomain || 'localhost').toUpperCase();
  if (toggles[`${domain}.FALLBACK-ACTIVE`] !== true) return 'legacy';

  const normalized = normalizePath(resolveTemplatePath(pathname, registry));
  if (toggles[`${domain}.FALLBACK-ACTIVE.${normalized}`] !== true) return 'legacy';

  return isRegistered(pathname, registry) ? 'shell' : 'legacy';
}

// --------------------------------------------------------------------------------------
// Loop guard. Both the shell and the legacy redirect on load; with stale-while-revalidate
// caches they can momentarily disagree and ping-pong. Track boundary crossings per path in
// sessionStorage and, after N within a short window, stop redirecting (render in place).
// --------------------------------------------------------------------------------------
const LOOP_KEY = 'zing:smartnav:bounces';
const LOOP_MAX = 2;
const LOOP_WINDOW_MS = 4000;

type LoopState = { path: string; count: number; ts: number };

/** Returns true if redirecting is still allowed; false if the loop guard tripped. */
export function allowRedirect(pathname: string, storage?: Storage): boolean {
  const store = storage ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
  if (!store) return true;
  let state: LoopState | null = null;
  try {
    state = JSON.parse(store.getItem(LOOP_KEY) || 'null');
  } catch {
    state = null;
  }
  const now = Date.now();
  if (!state || state.path !== pathname || now - state.ts > LOOP_WINDOW_MS) {
    state = { path: pathname, count: 0, ts: now };
  }
  if (state.count >= LOOP_MAX) {
    // Tripped: clear so a later genuine navigation starts fresh.
    try { store.removeItem(LOOP_KEY); } catch { /* ignore */ }
    return false;
  }
  state.count += 1;
  state.ts = now;
  try { store.setItem(LOOP_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  return true;
}

/** Clear the bounce counter once a page renders without redirecting (settled). */
export function clearLoopGuard(storage?: Storage): void {
  const store = storage ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : undefined);
  try { store?.removeItem(LOOP_KEY); } catch { /* ignore */ }
}
